// src/components/personal/sections/OrdersSection/hooks/useOrderData.ts
import { useMemo, useCallback, useEffect } from 'react';
import { Order, OrderFilters, CacheInfo } from '../types/types';
import { useOrderDataCore } from './useOrderDataCore';
import { useOrderPagination } from './useOrderPagination';
import { useOrderFilters } from './useOrderFilters';
import { useOrderSearch } from './useOrderSearch';
import { useOrderSorting } from './useOrderSorting';

// Объявление глобальных переменных для TypeScript
declare global {
  interface Window {
    __ACTIVE_FILTERS_CHECK: () => boolean;
    __FILTERS_CHANGED_CALLBACK: ((isFiltering: boolean) => void) | null;
    __BLOCK_ORDER_PAGE_LOAD: boolean;
  }
}

// Инициализация глобальных переменных
if (typeof window !== 'undefined') {
  window.__ACTIVE_FILTERS_CHECK = () => false;
  window.__FILTERS_CHANGED_CALLBACK = null;
  window.__BLOCK_ORDER_PAGE_LOAD = false;
}

// Интерфейс для параметров хука
export interface UseOrderDataProps {
  initialOrders?: Order[];
}

// Интерфейс для возвращаемого значения хука
export interface UseOrderDataReturn {
  // Данные заказов
  allOrders: Order[];
  filteredOrders: Order[];
  displayedOrders: Order[];
  
  // Состояние загрузки
  isLoading: boolean;
  error: string | null;
  allOrdersLoaded: boolean;
  totalOrdersCount: number;
  
  // Методы для загрузки данных
  loadAllOrders: () => Promise<void>;
  loadPage: (page: number, size?: number) => Promise<void>;
  refreshOrders: (forceRefresh?: boolean) => Promise<void>;
  exportOrdersToCSV: (filteredOrders: Order[]) => Promise<boolean>;
  
  // Фильтрация
  filters: OrderFilters;
  searchQuery: string;
  showFilters: boolean;
  updateFilters: (newFilters: Partial<OrderFilters>) => void;
  setSearchQuery: (query: string) => void;
  toggleFilters: () => void;
  isFiltering: boolean;
  resetFilters: () => void;
  clearCache: () => void;
  
  // Пагинация
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  
  // Сортировка
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  setSorting: (column: string, direction: 'asc' | 'desc') => void;
  
  // Дополнительные данные
  cacheInfo: CacheInfo | null;
}

// Функция для поддержки обоих вариантов вызова
export function useOrderData(props: UseOrderDataProps | Order[] = []): UseOrderDataReturn {
  // Определяем, какой формат аргументов использован
  const initialOrders = Array.isArray(props) ? props : props.initialOrders || [];
  
  return useOrderDataImplementation({ initialOrders });
}

// Основная реализация хука
export function useOrderDataImplementation({ 
  initialOrders = [] 
}: UseOrderDataProps): UseOrderDataReturn {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Используем базовый хук для загрузки данных
  const {
    orders: coreOrders,
    isLoading,
    error,
    allOrdersLoaded,
    totalOrdersCount,
    loadAllOrders: coreLoadAllOrders,
    loadOrderPage,
    refreshOrders: coreRefreshOrders,
    exportOrdersToCSV,
    clearCache,
    cacheInfo
  } = useOrderDataCore(initialOrders);
  
  // Используем хук для фильтрации
  const {
    filters,
    filteredOrders: ordersAfterFilters,
    isFiltering,
    showFilters,
    updateFilters,
    resetFilters,
    toggleFilters
  } = useOrderFilters({
    orders: coreOrders
  });
  
  // Используем хук для поиска
  const {
    searchQuery,
    searchResults: ordersAfterSearch,
    setSearchQuery
  } = useOrderSearch({
    orders: ordersAfterFilters
  });
  
  // Установка функции проверки активных фильтров для использования в других компонентах
  useEffect(() => {
    window.__ACTIVE_FILTERS_CHECK = () => isFiltering || searchQuery.trim() !== '';
    return () => {
      window.__ACTIVE_FILTERS_CHECK = () => false;
    };
  }, [isFiltering, searchQuery]);
  
  // В useOrderDataCore добавьте подписку на изменение фильтров
  useEffect(() => {
    // Сохраняем предыдущее значение переменной
    const prevCallback = window.__FILTERS_CHANGED_CALLBACK;
    
    // Устанавливаем нашу функцию
    window.__FILTERS_CHANGED_CALLBACK = (isFiltering) => {
      if (isFiltering) {
        // Если фильтры активны, блокируем загрузку страниц
        window.__BLOCK_ORDER_PAGE_LOAD = true;
      } else {
        window.__BLOCK_ORDER_PAGE_LOAD = false;
      }
    };
    
    return () => {
      // Восстанавливаем предыдущее значение при размонтировании
      window.__FILTERS_CHANGED_CALLBACK = prevCallback;
    };
  }, []);
  
  // Вычисляем общий список с применением фильтров и поиска
  const processedOrders = useMemo(() => {
    // Если есть строка поиска, применяем поиск к отфильтрованным данным
    if (searchQuery) {
      if (DEBUG) console.log(`Применение поиска "${searchQuery}" к ${ordersAfterFilters.length} заказам`);
      return ordersAfterSearch;
    }
    
    // Иначе возвращаем только отфильтрованные данные
    return ordersAfterFilters;
  }, [ordersAfterFilters, ordersAfterSearch, searchQuery, DEBUG]);
  
  // Используем хук для сортировки
  const {
    sortColumn,
    sortDirection,
    sortedOrders,
    setSorting
  } = useOrderSorting({
    orders: processedOrders
  });
  
  // Используем хук для пагинации
  const {
    pagination,
    setCurrentPage,
    setPageSize,
    startIndex,
    endIndex
  } = useOrderPagination({
    totalItems: sortedOrders.length
  });
  
  // Вычисляем список отображаемых заказов (для текущей страницы)
  const displayedOrders = useMemo(() => {
    return sortedOrders.slice(startIndex, endIndex);
  }, [sortedOrders, startIndex, endIndex]);
  
  // Адаптеры методов для обратной совместимости
  
  // Метод загрузки всех заказов
  const loadAllOrders = useCallback(async () => {
    if (DEBUG) console.log('loadAllOrders - запуск');
    // Всегда загружаем всех заказов при вызове этого метода
    await coreLoadAllOrders();
  }, [coreLoadAllOrders, DEBUG]);
  
  // Метод загрузки страницы (исправленная версия)
  const loadPage = useCallback(async (page: number, size: number = pagination.pageSize) => {
    if (DEBUG) console.log(`loadPage - страница ${page}, размер ${size}, фильтрация: ${isFiltering}, поиск: ${searchQuery}`);
    
    // Принудительно блокируем загрузку страницы, если активны фильтры или поиск
    if (isFiltering || searchQuery.trim() !== '') {
      if (DEBUG) console.log('loadPage - БЛОКИРОВКА загрузки страницы при активных фильтрах, используем кешированные данные');
      
      // Только устанавливаем параметры пагинации, без загрузки данных
      setCurrentPage(page);
      setPageSize(size);
      return;
    }
    
    // Иначе стандартное поведение - загружаем указанную страницу
    const offset = (page - 1) * size;
    await loadOrderPage(offset, size);
    setCurrentPage(page);
    setPageSize(size);
  }, [loadOrderPage, setCurrentPage, setPageSize, pagination.pageSize, isFiltering, searchQuery, DEBUG]);
  
  // Метод обновления заказов
  const refreshOrders = useCallback(async (forceRefresh: boolean = false) => {
    if (DEBUG) console.log(`refreshOrders - forceRefresh: ${forceRefresh}, фильтрация: ${isFiltering}, поиск: ${searchQuery}`);
    
    // Всегда загружаем всех заказов, если активна фильтрация или поиск
    const needAllOrders = isFiltering || searchQuery.trim() !== '';
    
    if (needAllOrders) {
      if (DEBUG) console.log('refreshOrders - принудительная загрузка всех заказов из-за активных фильтров');
      await coreRefreshOrders(forceRefresh, true); // Второй аргумент - принудительная загрузка всех заказов
    } else {
      await coreRefreshOrders(forceRefresh, false);
    }
    
    // Сбрасываем пагинацию на первую страницу при обновлении
    if (forceRefresh) {
      setCurrentPage(1);
    }
  }, [coreRefreshOrders, setCurrentPage, isFiltering, searchQuery, DEBUG]);
  
  return {
    // Данные о заказах
    allOrders: coreOrders,
    filteredOrders: sortedOrders, // Все заказы после фильтрации, поиска и сортировки
    displayedOrders, // Заказы на текущей странице
    
    // Состояние загрузки
    isLoading,
    error,
    allOrdersLoaded,
    totalOrdersCount,
    
    // Методы для загрузки данных
    loadAllOrders,
    loadPage,
    refreshOrders,
    exportOrdersToCSV,
    
    // Фильтрация и поиск
    filters,
    searchQuery,
    showFilters,
    updateFilters,
    setSearchQuery,
    toggleFilters,
    isFiltering,
    resetFilters,
    clearCache,
    
    // Пагинация
    pagination,
    setCurrentPage,
    setPageSize,
    
    // Сортировка
    sortColumn,
    sortDirection,
    setSorting,
    
    // Дополнительная информация
    cacheInfo
  };
}

// Для обратной совместимости
export const useOrdersData = useOrderData;